import React, { useEffect, useRef, useState } from "react";
import { Calendar as CalendarIcon, X } from "lucide-react";

/* =======================
   Styles
======================= */
import styles from "./DateRangePicker.module.scss";const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const formatIso = (d) => {
  if (!d) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatReadable = (d) =>
  d
    ? d.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "";

const getYears = () => {
  const years = [];
  const current = new Date().getFullYear();
  for (let i = current - 20; i <= current + 20; i++) {
    years.push(i);
  }
  return years;
};

/* ===================== CALENDAR ===================== */
function Calendar({ monthDate, onSelect, selectedDate, maxDate }) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();

  const todayEnd = maxDate
    ? new Date(new Date(maxDate).setHours(23, 59, 59, 999))
    : null;

  const isFutureDate = (date) => (todayEnd ? date > todayEnd : false);

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const weeks = [];
  let day = 1;

  for (let w = 0; w < 6; w++) {
    const week = [];
    for (let d = 0; d < 7; d++) {
      const cellIndex = w * 7 + d;
      const showDay = cellIndex >= firstDay && day <= daysInMonth;

      if (showDay) {
        const cellDate = new Date(year, month, day);
        const disabled = isFutureDate(cellDate);

        week.push({
          date: cellDate,
          day,
          disabled,
          selected:
            selectedDate &&
            selectedDate.getFullYear() === year &&
            selectedDate.getMonth() === month &&
            selectedDate.getDate() === day,
        });
        day++;
      } else {
        week.push({ day: "", disabled: true });
      }
    }
    weeks.push(week);
  }

  return (
    <div className={styles["admin-drp__calendar"]}>
      <div className={styles["admin-drp__weekdays"]}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className={styles["admin-drp__weekday"]}>
            {d}
          </div>
        ))}
      </div>

      <div className={styles["admin-drp__weeks"]}>
        {weeks.map((week, wi) => (
          <div key={wi} className={styles["admin-drp__week"]}>
            {week.map((cell, ci) => (
              <button
                key={ci}
                className={`${styles["admin-drp__day"]} ${
                  cell.selected ? styles["admin-drp__day--selected"] : ""
                } ${cell.disabled ? styles["admin-drp__day--disabled"] : ""}`}
                disabled={cell.disabled || !cell.day}
                onClick={() =>
                  !cell.disabled && cell.date && onSelect(cell.date)
                }
              >
                {cell.day}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ===================== DATE RANGE PICKER ===================== */
const DateRangePicker = ({ onChange = () => {}, maxDate }) => {
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);

  const [openPicker, setOpenPicker] = useState(null);
  const [popupPos, setPopupPos] = useState("bottom");

  const [fromView, setFromView] = useState(new Date());
  const [toView, setToView] = useState(new Date());

  const years = getYears();
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpenPicker(null);
      }
    };
    window.addEventListener("mousedown", handleOutside);
    return () => window.removeEventListener("mousedown", handleOutside);
  }, []);

  const handleOpen = (type) => {
    setOpenPicker(type);

    setTimeout(() => {
      if (!wrapperRef.current) return;
      const box = wrapperRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - box.bottom;
      const spaceAbove = box.top;
      setPopupPos(
        spaceBelow < 380 && spaceAbove > spaceBelow ? "top" : "bottom",
      );
    }, 10);
  };

  /*
    Emit onChange ONLY when the actual date values change.
    Without this guard, an inline `onChange` prop from the parent
    creates a new function reference on every render, which causes
    this effect to re-run and re-fire `onChange("from", "")` /
    `onChange("to", "")` — silently resetting any parent state
    (e.g. page = 1) and triggering a duplicate API call.
  */
  const lastEmittedRef = useRef({ from: undefined, to: undefined });
  useEffect(() => {
    const fromIso = from ? formatIso(from) : "";
    const toIso = to ? formatIso(to) : "";

    if (lastEmittedRef.current.from !== fromIso) {
      lastEmittedRef.current.from = fromIso;
      onChange("from", fromIso);
    }
    if (lastEmittedRef.current.to !== toIso) {
      lastEmittedRef.current.to = toIso;
      onChange("to", toIso);
    }
  }, [from, to, onChange]);

  const handleMonthChange = (type, value) => {
    const view = type === "from" ? fromView : toView;
    const updated = new Date(view.getFullYear(), value, 1);
    type === "from" ? setFromView(updated) : setToView(updated);
  };

  const handleYearChange = (type, value) => {
    const view = type === "from" ? fromView : toView;
    const updated = new Date(value, view.getMonth(), 1);
    type === "from" ? setFromView(updated) : setToView(updated);
  };

  return (
    <div className={styles["admin-drp__wrapper"]} ref={wrapperRef}>
      {/* FROM */}
      <div
        className={styles["admin-drp__field"]}
        onClick={() => handleOpen("from")}
      >
        <CalendarIcon size={18} className={styles["admin-drp__icon"]} />
        <input
          readOnly
          value={from ? formatReadable(from) : ""}
          placeholder="From"
          className={styles["admin-drp__input"]}
        />
      </div>

      <div className={styles["admin-drp__separator"]}>—</div>

      {/* TO */}
      <div
        className={styles["admin-drp__field"]}
        onClick={() => handleOpen("to")}
      >
        <CalendarIcon size={18} className={styles["admin-drp__icon"]} />
        <input
          readOnly
          value={to ? formatReadable(to) : ""}
          placeholder="To"
          className={styles["admin-drp__input"]}
        />
      </div>

      {/* CLEAR — shown once any date is picked; emits "" for both bounds */}
      {(from || to) && (
        <button
          type="button"
          className={styles["admin-drp__clear"]}
          data-tooltip="Clear dates"
          aria-label="Clear date range"
          onClick={() => {
            setFrom(null);
            setTo(null);
            setOpenPicker(null);
          }}
        >
          <X size={14} strokeWidth={2.5} />
        </button>
      )}

      {/* FROM POPUP */}
      {openPicker === "from" && (
        <div
          className={`${styles["admin-drp__popup"]} ${
            styles[`admin-drp__popup--${popupPos}`]
          }`}
        >
          <div className={styles["admin-drp__dropdowns"]}>
            <select
              className={styles["admin-drp__select"]}
              value={fromView.getMonth()}
              onChange={(e) => handleMonthChange("from", +e.target.value)}
            >
              {months.map((m, i) => (
                <option key={i} value={i}>
                  {m}
                </option>
              ))}
            </select>

            <select
              className={styles["admin-drp__select"]}
              value={fromView.getFullYear()}
              onChange={(e) => handleYearChange("from", +e.target.value)}
            >
              {years.map((y) => (
                <option key={y}>{y}</option>
              ))}
            </select>
          </div>

          <Calendar
            monthDate={fromView}
            selectedDate={from}
            maxDate={maxDate}
            onSelect={(date) => {
              setFrom(date);
              if (to && date > to) setTo(null);
              setOpenPicker(null);
            }}
          />
        </div>
      )}

      {/* TO POPUP */}
      {openPicker === "to" && (
        <div
          className={`${styles["admin-drp__popup"]} ${
            styles[`admin-drp__popup--${popupPos}`]
          }`}
        >
          <div className={styles["admin-drp__dropdowns"]}>
            <select
              className={styles["admin-drp__select"]}
              value={toView.getMonth()}
              onChange={(e) => handleMonthChange("to", +e.target.value)}
            >
              {months.map((m, i) => (
                <option key={i} value={i}>
                  {m}
                </option>
              ))}
            </select>

            <select
              className={styles["admin-drp__select"]}
              value={toView.getFullYear()}
              onChange={(e) => handleYearChange("to", +e.target.value)}
            >
              {years.map((y) => (
                <option key={y}>{y}</option>
              ))}
            </select>
          </div>

          <Calendar
            monthDate={toView}
            selectedDate={to}
            maxDate={maxDate}
            onSelect={(date) => {
              setTo(date);
              if (from && date < from) setFrom(date);
              setOpenPicker(null);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
